/**
 * Kurva mana yang sejenis, dan karena itu boleh ditumpuk pada satu sumbu.
 *
 * Jendela pemeriksa kurva bisa menggambar satu kurva pembanding di belakang
 * kurva yang sedang dibuka. Sampai sekarang yang ditawarkannya hanyalah
 * **mnemonic yang sama dari set lain** — dua versi PHIE, dua kali hitung
 * dengan parameter berbeda. Itu perbandingan yang paling sering dilakukan,
 * tetapi bukan satu-satunya yang masuk akal: PHIE dibaca terhadap PHIT untuk
 * melihat berapa yang dimakan serpih, VSH terhadap VCL, SW terhadap SWE.
 *
 * Yang **tidak** boleh ditawarkan adalah kurva yang tidak sebanding. Menumpuk
 * GR pada sumbu PHIE menggambar garis yang bentuknya tidak berarti apa-apa —
 * bukan karena gambarnya salah, melainkan karena keduanya diukur dalam besaran
 * yang berbeda dan satu kotak hanya punya satu sumbu. Karena itu kekerabatan
 * di sini ditentukan oleh **besaran yang diukur**, bukan oleh kemiripan nama:
 * dua kurva sejenis bila keduanya menjawab pertanyaan yang sama dalam satuan
 * yang sama.
 *
 * Daftarnya sengaja pendek dan konservatif. Sebuah pemilih yang menawarkan
 * dua puluh kurva memaksa penggunanya menyaring sendiri, dan yang paling
 * mudah tersaring salah adalah yang paling jarang dipakai.
 */

/**
 * Keluarga besaran. Tiap baris adalah satu besaran yang diukur, dengan
 * mnemonic-mnemonic yang lazim menamainya.
 *
 * Nama di sini adalah nama yang benar-benar dipakai di lapangan, termasuk
 * ejaan yang berbeda untuk hal yang sama (PHIT dan PHI_T). Yang tidak ada di
 * daftar ini tetap punya kerabat — dirinya sendiri di set lain — sebab itulah
 * perbandingan yang selalu sah.
 */
export const CURVE_FAMILIES: Record<string, string[]> = {
  /** Volume serpih dan lempung. Keduanya pecahan 0–1 atau persen. */
  vshale: ["VSH", "VCL", "VSHALE", "VSH_GR", "VSH_ND", "VCLAY"],
  /** Porositas — total, efektif, dan yang diturunkan tiap alat. */
  porositas: ["PHIE", "PHIT", "PHI", "PHIS", "PHID", "PHIN", "PHI_T", "PHI_E", "NPHI", "PHIA"],
  /** Saturasi air. */
  saturasi: ["SW", "SWE", "SWT", "SWIRR", "SXO", "SW_ARCHIE"],
  /** Permeabilitas. Dibaca pada dekade, jadi sumbunya lazim logaritmik. */
  permeabilitas: ["PERM", "K", "KINT", "KH", "PERM_TIMUR"],
  /** Resistivitas. */
  resistivitas: ["RT", "RXO", "RES", "ILD", "LLD", "LLS", "MSFL"],
  /** Densitas curah. */
  densitas: ["RHOB", "RHOZ", "DEN"],
  /** Sinar gamma. */
  gamma: ["GR", "SGR", "CGR", "GRC"],
  /** Waktu tempuh sonik. */
  sonik: ["DTC", "DT", "DTS", "DTCO"],
  /** Kaliper. */
  kaliper: ["CALI", "CAL", "HCAL"],
  /**
   * Jenis batuan. Keduanya nomor kelas dari penomoran yang sama — yang satu
   * dihitung dari porositas dan permeabilitas, yang lain ditebak dari kurva
   * log — dan menumpuknya adalah satu-satunya cara melihat di mana tebakannya
   * meleset pada sumur yang kebetulan punya keduanya.
   */
  rocktype: ["ROCKTYPE", "ROCKTYPE_ML", "RRT", "HFU"],
};

/** Kurva yang dihasilkan tiap tahap alur deterministik, urut tahapnya. */
export const STAGE_CURVES: Record<number, string> = {
  1: "VSH",
  2: "PHIT",
  3: "PHIE",
  4: "SW",
  5: "PERM",
  // Tahap enam tidak menghasilkan kurva: ia menerapkan tiga ambang atas kurva
  // yang sudah ada. Yang keluar darinya adalah net pay — sebuah angka per
  // zona, bukan sederet nilai per kedalaman — jadi tidak ada yang bisa dibuka
  // di jendela pemeriksa kurva.
};

/** Nama keluarga sebuah mnemonic, atau `null` bila ia tidak terdaftar. */
export function familyOf(mnemonic: string): string | null {
  const nama = mnemonic.trim().toUpperCase();
  for (const [keluarga, anggota] of Object.entries(CURVE_FAMILIES)) {
    if (anggota.includes(nama)) return keluarga;
  }
  return null;
}

/** Apakah dua mnemonic mengukur besaran yang sama? */
export function sameKin(a: string, b: string): boolean {
  if (a.trim().toUpperCase() === b.trim().toUpperCase()) return true;
  const ka = familyOf(a);
  return ka !== null && ka === familyOf(b);
}

export interface KinCurve {
  id: number;
  mnemonic: string;
  /** Nama set beserta versinya, sebagaimana tercetak di pemilih. */
  setLabel: string;
  /** Set yang sedang aktif tercetak lebih dulu. */
  active?: boolean;
  /**
   * Log ini dimatikan — tidak dipakai menghitung apa pun di aplikasi ini.
   *
   * Tetap ditawarkan di sini, dan itu satu-satunya pemilih yang begitu. Sebabnya
   * ada pada apa yang dikerjakan jendela ini: memeriksa sebuah kurva justru
   * tindakan yang dipakai orang **memutuskan** apakah sebuah log layak dipakai,
   * dan sebuah pemilih yang menyembunyikan log yang sudah dimatikan membuat
   * keputusan itu tidak bisa ditinjau kembali oleh siapa pun — termasuk oleh
   * yang membuatnya. Yang dijaga bukan ketiadaannya melainkan ketidakjelasannya:
   * labelnya menyebut "off", supaya tidak ada yang membandingkan kurvanya dengan
   * sesuatu yang ia sangka masih ikut dihitung.
   */
  off?: boolean;
}

/**
 * Kurva yang pantas ditawarkan sebagai pembanding bagi `mnemonic`.
 *
 * Urutannya bukan hiasan. Yang paling sering dicari orang adalah **mnemonic
 * yang sama dari set lain** — dua kali hitung dengan parameter berbeda — jadi
 * itu yang berdiri di puncak. Kerabat sekeluarga menyusul, sebab
 * membandingkan PHIE dengan PHIT adalah pertanyaan yang berbeda dan lebih
 * jarang. Sebuah pemilih yang mengurutkannya menurut abjad menaruh jawaban
 * yang paling dicari di tengah daftar.
 *
 * Kurva yang sedang dibuka dibuang dari daftar: ia tidak bisa menjadi
 * pembanding bagi dirinya sendiri, dan sebuah pilihan yang menggambar dua
 * garis identik terbaca sebagai kurva yang tidak berubah padahal tidak ada
 * yang dibandingkan.
 */
export function kinOptions(
  mnemonic: string,
  curveId: number,
  semua: KinCurve[],
): { id: number; label: string }[] {
  const nama = mnemonic.trim().toUpperCase();
  const layak = semua.filter(
    (c) => c.id !== curveId && sameKin(mnemonic, c.mnemonic),
  );

  const peringkat = (c: KinCurve) => {
    const sama = c.mnemonic.trim().toUpperCase() === nama;
    // Mnemonic sama lebih dulu; di dalam masing-masing, set aktif lebih dulu,
    // dan yang dimatikan turun ke dasar tiap kelompoknya: ia tetap boleh
    // dibandingkan, tetapi ia bukan yang dicari orang ketika ia membuka daftar.
    return (sama ? 0 : 4) + (c.active ? 0 : 2) + (c.off ? 1 : 0);
  };

  return [...layak]
    .sort((a, b) => peringkat(a) - peringkat(b))
    .map((c) => ({
      id: c.id,
      // Mnemonic disebut hanya ketika ia berbeda: pada daftar yang seluruhnya
      // PHIE, mencetak "PHIE" di setiap baris memakan lebar yang dibutuhkan
      // nama setnya, dan justru nama set itulah yang membedakan barisnya.
      label:
        (c.mnemonic.trim().toUpperCase() === nama
          ? c.setLabel
          : `${c.mnemonic} — ${c.setLabel}`) + (c.off ? " · off" : ""),
    }));
}
